import { RolesApi } from "@/Administration/Api/Roles/RolesApi";
import { AdminServiceGridSortColumn } from "@/Administration/Models/Enums/AdminServiceGridSortColumn";
import { RoleHeaderModel } from "@/Administration/Models/Role/RoleHeaderModel";
import { UserEditMode } from "@/Administration/Models/UI/Users/UserEditMode";
import { Client } from "@/Common/Api/Client";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { EmployeeGroup } from "@/Common/Api/Employee/Dto/EmployeeGroup";
import { Permission } from "@/Common/Api/Employee/Dto/Permission";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { RadioButtonv2 } from "@/Common/Components/Inputs/RadioButtonv2";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { SpanControl } from "@/Common/Components/SpanControl";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { IAllowlistModel } from "@/Common/Models/Administration/IAllowlistModel";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { PrescriptionPrintType } from "@/Common/Models/Employee/Enums/Settings";
import { EnumMyleLoginPermission } from "@/Common/Models/EnumMyleLoginPermission";
import { Guid } from "@/Common/Models/Guid";
import { IComboOrServiceItem } from "@/Common/Models/Services/BaseServiceDefinitionModel";
import { ServiceModel } from "@/Common/Models/Services/ServiceModel";
import { ServiceOrComboTableItem } from "@/Common/Models/Services/ServiceOrComboTableItem";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { FormMode, SortDirection } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { PermissionDependencyResolver } from "../../Helpers/PermissionDependencyResolver";
import { RoleListFieldComponent } from "../Roles/Field/RolesListFieldComponent";
import { ProtectMedicalNoteComponent } from "./Allowlist/ProtectMedicalNoteComponent";
import { PermissionSettingsSectionComponent } from "./PermissionSettings/PermissionSettingsSectionComponent";
import { UserConfigurationSectionComponent } from "./PermissionSettings/UserConfigurationSectionComponent";
import { GroupSettingsComponent } from "./GroupSettingsComponent";
import { BaseEditableUserInfoPanelProps } from "./UserDetailsPanelComponent";

import styles from "./UserConfigurationInfoEditComponent.module.scss";

class UserConfigurationInfoEditComponentState {
    public isUserNameValid: boolean;
    public isTemporaryPasswordValid: boolean;
    public isInactivityTimeoutValid: boolean;
    public isPasswordValidityValid: boolean;
    public isStartDateValid: boolean;
    public isEndDateValid: boolean;
    public canSaveSettings: boolean;
    public isSiteValid: boolean;
    public isPermissionValid: boolean;
    public roleDefinitions: RoleHeaderModel[];
}

class UserConfigurationInfoEditComponent extends React.Component<BaseEditableUserInfoPanelProps, UserConfigurationInfoEditComponentState> {
    private c1Paddingtop: React.CSSProperties = { paddingTop: "5px" };
    private redx: React.CSSProperties = { color: "red", fontWeight: "bold" };
    private paddingBotton: React.CSSProperties = { paddingBottom: "5px" };

    private saveOptionKey: string = "EmployeeServiceComboGrid";

    constructor(props: BaseEditableUserInfoPanelProps) {
        super(props);

        const user = props.editedUser;
        if (!user.Model.PreferredOfficialLanguageLookupId) {
            user.Model.PreferredOfficialLanguageLookupId = sessionStore.getCurrentEmployee().Model.PreferredOfficialLanguageLookupId;
        }

        const initialState = new UserConfigurationInfoEditComponentState();
        initialState.isInactivityTimeoutValid = false;
        initialState.isPasswordValidityValid = false;
        initialState.isStartDateValid = false;
        initialState.isEndDateValid = false;
        initialState.isUserNameValid = Employee.isUserNameValid(user.Model);
        initialState.isSiteValid = Employee.isSitesValid(user.Model);
        initialState.isPermissionValid = Employee.isPermissionValid(user.Model);

        if (props.model.get("UserEditMode") == UserEditMode.Create)
            initialState.isTemporaryPasswordValid = Employee.isTemporaryPasswordValid(user.Model);

        initialState.isStartDateValid = Employee.isStartDateValid(user.Model);
        initialState.isEndDateValid = Employee.isEndDateValid(user.Model);
        initialState.isInactivityTimeoutValid = Employee.isTimeoutDelayValid(user.Model);

        if (!user.Model.Permissions || user.Model.Permissions == null)
            this.permissionsByDefault(user.Model);

        initialState.canSaveSettings = false;
        this.state = initialState;
    }

    public async componentDidMount(): Promise<void> {
        if (cacheStore.ClinicSettings.EnableRoleBasedAccessControl)
            await this.loadRolePermissionHeaders();

        this.mutateState(s => {
            s.canSaveSettings = this.isValid(s);
        });

        $(window).trigger("resize");
    }

    public componentDidUpdate(prevProps: BaseEditableUserInfoPanelProps): void {
        if (this.props.editedUser !== prevProps.editedUser) {
            const user = this.props.editedUser;
            if (!user.Model.PreferredOfficialLanguageLookupId)
                user.Model.PreferredOfficialLanguageLookupId = sessionStore.getCurrentEmployee().Model.PreferredOfficialLanguageLookupId;

            this.mutateState(s => {
                s.isEndDateValid = Employee.isEndDateValid(user.Model);
                s.isStartDateValid = Employee.isStartDateValid(user.Model);
                s.isUserNameValid = Employee.isUserNameValid(user.Model);
                s.isSiteValid = Employee.isSitesValid(user.Model);
                s.isPermissionValid = Employee.isPermissionValid(user.Model) || this.getResolvedRolePermissions(user.Model.Roles, s.roleDefinitions)?.any();
                if (this.props.model.get("UserEditMode") == UserEditMode.Create)
                    s.isTemporaryPasswordValid = Employee.isTemporaryPasswordValid(user.Model);

                s.isInactivityTimeoutValid = Employee.isTimeoutDelayValid(user.Model);
                s.canSaveSettings = this.isValid(s);
            });
        }
    }

    public render(): JSX.Element {
        const employeeType: EmployeeType = this.props.editedUser.Model.Type;

        // settings UI improvement and role-based access control are not supported by API users
        const showUserSettingsUiImprovements = cacheStore.ClinicSettings.EnableUserSettingsUiImprovements && employeeType !== EmployeeType.Api;
        const showRoleBasedAccessControl = cacheStore.ClinicSettings.EnableRoleBasedAccessControl && employeeType !== EmployeeType.Api;

        return (
            <div>
                {employeeType !== EmployeeType.Api && (
                    <>
                        <div className="user-component">
                            <div className="flex-container heading" style={{ "borderBottom": "solid 1px #ccc", "margin": "20px 25px" }}>
                                <div className="h">
                                    <h3 className="thin-font">{cacheStore.GetString("AccountConfigurationLabel")}</h3>
                                </div>
                            </div>
                            <div className="psingle-column practice centered">
                                <div className="tb">
                                    {this.renderConfiguration()}
                                </div>
                            </div>

                        </div>

                        <div className="user-component">
                            <div className="flex-container heading" style={{ "borderBottom": "solid 1px #ccc", "margin": "20px 25px" }}>
                                <div className="h">
                                    <h3 className="thin-font">{cacheStore.GetString("PrintingOptionLabel")}</h3>
                                </div>
                            </div>
                            <div className="psingle-column practice centered">
                                <div className="tb">
                                    {this.renderPrintingOption()}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <div className="user-component">
                    <div className="flex-container heading" style={{ "borderBottom": "solid 1px #ccc", "margin": "20px 25px" }}>
                        <div className="h">
                            <h3 className="thin-font">{cacheStore.GetString("AccessSettingsLabel")}</h3>
                        </div>
                    </div>
                    <div className="psingle-column practice centered">
                        <div className="tb">
                            {this.renderAccessSettings(showUserSettingsUiImprovements, showRoleBasedAccessControl)}
                        </div>
                    </div>
                </div>
                {(showUserSettingsUiImprovements) && (
                    <UserConfigurationSectionComponent
                        title={cacheStore.GetString("PermissionsLabel")}
                        headerClassName={styles.headerWithActionLink}
                        headerActionLink={this.getPermissionsHeaderActionButton()}
                        className={(!this.state.isPermissionValid ? styles.permissionsAreInvalid : null)}
                    >
                        {this.renderPermissions()}
                    </UserConfigurationSectionComponent>
                )}
                {(employeeType !== EmployeeType.Assistant && employeeType !== EmployeeType.Api) && (
                    <div className="user-component">
                        <div className="flex-container heading" style={{ "margin": "20px 25px" }}>
                            <div className="h">
                                <h3 className="thin-font">{cacheStore.GetString("ServiceComboLabel")}</h3>
                            </div>
                            <div style={{ marginTop: "10px" }}>
                                <div className="flex-container search-settings" style={{ "justifyContent": "space-between" }}>
                                    <a
                                        style={{ "textDecoration": "underline", "marginRight": "10px" }}
                                        onClick={() =>
                                            sessionStore.dispatch(LocalOperations.AdministrationUsers.OpenAddServiceCombo)
                                        }
                                    >
                                        + {cacheStore.GetString("AddNewComboServiceLabel")}
                                    </a>
                                </div>
                            </div>

                        </div>
                        <div>
                            {this.renderServices()}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private renderConfiguration(): JSX.Element {
        let employee = this.props.editedUser;
        this.getUserConfigurationLanguage(employee);
        const displayTemporaryPasswordField = this.props.model.get("UserEditMode") == UserEditMode.Create && !cacheStore.ClinicSettings.EnableSSOAuthentication;

        return (
            <div className="tbc">
                <div>
                    <table>
                        <tbody>
                            {this.props.model.get("UserEditMode") == UserEditMode.Create && (
                                <tr>
                                    <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("UserNameLabel")}</label></td>
                                    <td className="c2">
                                        <input type="text" style={{ display: "none" }} />
                                        <TextboxControl
                                            id="UserName_Add_Admin"
                                            type="text"
                                            name="username_admin"
                                            value={employee.Model.UserName ? employee.Model.UserName : ""}
                                            className="form-control"
                                            onChange={e => this.props.mutateParentEmployee(emp => emp.Model.UserName = e)}
                                            valid={this.state.isUserNameValid}
                                            autoComplete="nope"
                                            onClickRestrictedSearch={false}
                                            autoCompleteItems={null}
                                            dataCy="userProfile-settingsUserName"
                                        />
                                    </td>
                                </tr>
                            )}
                            {displayTemporaryPasswordField && (
                                <tr>
                                    <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("TemporaryPasswordLabel")}</label></td>
                                    <td className="c2">
                                        <TextboxControl
                                            id="Password_Add_Admin"
                                            type="password"
                                            name="password_admin"
                                            value={employee.Model.Password ? employee.Model.Password : ""}
                                            className="form-control"
                                            onChange={e => this.props.mutateParentEmployee(emp => {
                                                emp.Model.Password = e;
                                                if (!IsNullOrWhiteSpace(emp.Model.Password))
                                                    emp.Model.IsTemporaryPassword = true;
                                            })}
                                            valid={this.state.isTemporaryPasswordValid}
                                            autoComplete="new-password"
                                            onClickRestrictedSearch={false}
                                            autoCompleteItems={null}
                                            dataCy="userProfile-settingsTempPwd"
                                        />

                                    </td>
                                </tr>
                            )}
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("LanguageLabel")}</label></td>
                                <td className="c2">
                                    <SelectLookupControl
                                        IncludeEmptyChoice={false}
                                        Value={employee.Model.PreferredOfficialLanguageLookupId}
                                        AvailableValues={cacheStore.GetLanguagesLookup().map(x => x.ToComboboxItem())}
                                        Callback={lookupId => this.props.mutateParentEmployee(employee => {
                                            employee.Model.PreferredOfficialLanguageLookupId = lookupId;

                                            const lookupCode = cacheStore.GetLanguagesLookup().firstOrDefault(lookup => lookup.LookupID === lookupId)?.LookupCode;
                                            switch (lookupCode) {
                                                case "FRENCH":
                                                    employee.Model.Language = "fr";
                                                    break;
                                                case "SPANISH":
                                                    employee.Model.Language = "es-MX";
                                                    break;
                                                default:
                                                    employee.Model.Language = "en";
                                                    break;
                                            }
                                        })}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("TimeoutInactivityLabel")}</label></td>
                                <td className="c2">
                                    <div style={{ "width": "85%", "float": "left" }}>
                                        <TextboxControl
                                            id="TimeoutInactivityEditAdd"
                                            maxLength={6}
                                            IsNumeric={true}
                                            value={(employee.Model.TimeoutDelay > 0) ? employee.Model.TimeoutDelay.toString() : ""}
                                            className="form-control"
                                            onChange={e => this.props.mutateParentEmployee(emp => emp.Model.TimeoutDelay = (parseInt(e) || 0))}
                                            valid={this.state.isInactivityTimeoutValid}
                                        />
                                    </div>
                                    <div style={{ "textAlign": "right", "paddingTop": "7px" }}> {cacheStore.GetString("MinuteLabel")}</div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("ValidityPasswordLabel")}</label></td>
                                <td className="c2">
                                    <div style={{ "width": "85%", "float": "left" }}>
                                        <TextboxControl
                                            id="ValidityDays_Password__EditAdd"
                                            maxLength={6}
                                            IsNumeric={true}
                                            value={(employee.Model.ValidityPeriodPasswordDays > 0) ? employee.Model.ValidityPeriodPasswordDays.toString() : ""}
                                            className="form-control"
                                            onChange={e => this.props.mutateParentEmployee(emp => emp.Model.ValidityPeriodPasswordDays = (parseInt(e) || 0))}
                                        />
                                    </div>
                                    <div style={{ "textAlign": "right", "paddingTop": "7px" }}>{cacheStore.GetString("DayssLabel")}</div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("StartValidityUserLabel")}</label></td>
                                <td className="c2">
                                    <DateSelector
                                        id="User_StartValidity_Date"
                                        width={200}
                                        cssClass="rounded"
                                        date={employee.Model.StartValidity}
                                        onChangeCallback={(date: Date) => this.props.mutateParentEmployee(emp => emp.Model.StartValidity = (date != null ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0) : null))}
                                        onSelectOp={null}
                                        dateFormat="yyyy/MM/dd"
                                        toDayEnabled={false}
                                        disabled={false}
                                        valid={this.state.isStartDateValid}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("EndValidityUserLabel")}</label></td>
                                <td className="c2">
                                    <DateSelector
                                        id="User_EndValidity_Date"
                                        width={200}
                                        cssClass="rounded"
                                        date={employee.Model.EndValidity}
                                        onChangeCallback={(date: Date) => this.props.mutateParentEmployee(emp => emp.Model.EndValidity = (date != null ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0) : null))}
                                        onSelectOp={null}
                                        dateFormat="yyyy/MM/dd"
                                        toDayEnabled={false}
                                        disabled={false}
                                        valid={this.state.isEndDateValid}
                                    />
                                </td>
                            </tr>
                            {(employee.Model.Type === EmployeeType.Practician || employee.Model.Type === EmployeeType.Admin) && (
                                <>
                                    <tr>
                                        <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("PublicApi_EmployeeVisibleOnlineLabel")}</label></td>
                                        <td className="c2">
                                            <CheckboxControl
                                                id="chkVisibleOnline"
                                                checked={employee.Model.VisibleOnline}
                                                onChange={(isChecked: boolean) => this.props.mutateParentEmployee(emp => emp.Model.VisibleOnline = isChecked)}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("BookingHubEnabledLabel")}</label></td>
                                        <td className="c2">
                                            <CheckboxControl
                                                id="chkBookingHubEnabled"
                                                checked={employee.Model.BookingHubEnabled}
                                                onChange={(isChecked: boolean) => this.props.mutateParentEmployee(emp => {
                                                    emp.Model.BookingHubEnabled = isChecked;
                                                    if (!isChecked) {
                                                        emp.Model.HubFirstName = null;
                                                        emp.Model.HubLastName = null;
                                                    }
                                                })}
                                            />
                                        </td>
                                    </tr>
                                    {employee.Model.BookingHubEnabled && (
                                        <>
                                            <tr>
                                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("HubFirstNameLabel")}</label></td>
                                                <td className="c2">
                                                    <TextboxControl
                                                        id="hubFirstName"
                                                        className="form-control"
                                                        disabled={!employee.Model.BookingHubEnabled}
                                                        value={employee.Model.HubFirstName ?? ""}
                                                        onChange={value => this.props.mutateParentEmployee(emp => emp.Model.HubFirstName = value)}
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("HubLastNameLabel")}</label></td>
                                                <td className="c2">
                                                    <TextboxControl
                                                        id="hubLastName"
                                                        className="form-control"
                                                        disabled={!employee.Model.BookingHubEnabled}
                                                        value={employee.Model.HubLastName ?? ""}
                                                        onChange={value => this.props.mutateParentEmployee(emp => emp.Model.HubLastName = value)}
                                                    />
                                                </td>
                                            </tr>
                                        </>
                                    )}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    private renderPrintingOption(): JSX.Element {
        let employee = this.props.editedUser;

        let selectedPrescriptionType = "regular_a";

        if (employee.Model.PrescriptionPrintType == PrescriptionPrintType.Thermal)
            selectedPrescriptionType = "thermal_a";
        else if (employee.Model.PrescriptionPrintType == PrescriptionPrintType.Mexico)
            selectedPrescriptionType = "mexico_a";

        return (
            <div className="tbc" style={this.paddingBotton}>
                <div>
                    <table>
                        <tbody>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("LabelsLabel")}</label></td>
                                <td className="c2">
                                    <div style={{ width: "600px" }}>
                                        <RadioButtonv2
                                            cssClass="settings-user-radioWidth"
                                            title={null}
                                            items={[
                                                new KeyValuePair("portrait", cacheStore.GetString("PortraitLabel")),
                                                new KeyValuePair("landscape", cacheStore.GetString("LandscapeLabel"))
                                            ].toImmutable()}
                                            htmlName="labels"
                                            onChangeCallback={item => this.onChangeLabels(item)}
                                            changeOperation={null}
                                            selectedItem={employee.Model.PrintingSetting === true ? "landscape" : "portrait"}
                                            styleForEachradio={{ width: "22%", float: "left", marginTop: "5px" }}
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("HeightSlashWidthLabel")}</label></td>
                                <td className="c2">
                                    <div style={{ width: "600px" }}>
                                        <RadioButtonv2
                                            cssClass="settings-user-radioWidth"
                                            title={null}
                                            items={[
                                                new KeyValuePair("small", cacheStore.GetString("ThreePerOneInchesLabel")),
                                                new KeyValuePair("large", cacheStore.GetString("FourPerOnePointFiveInchesLabel"))
                                            ].toImmutable()}
                                            htmlName="widthHeight"
                                            onChangeCallback={item => this.onChangeLargeLabelPrinting(item)}
                                            changeOperation={null}
                                            selectedItem={employee.Model.LargeLabelPrinting === false ? "small" : "large"}
                                            styleForEachradio={{ width: "22%", float: "left", marginTop: "5px" }}
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("PrescriptionLabel")}</label></td>
                                <td className="c2">
                                    <div style={{ width: "600px" }}>
                                        <RadioButtonv2
                                            cssClass="settings-user-radioWidth"
                                            title={null}
                                            items={[
                                                new KeyValuePair("regular_a", cacheStore.GetString("RegularPrinterLabel")),
                                                new KeyValuePair("thermal_a", cacheStore.GetString("ThermicPrinterLabel")),
                                                new KeyValuePair("mexico_a", cacheStore.GetString("MexicoAndColombiaStandardsLbl"))
                                            ].toImmutable()}
                                            htmlName="prescription"
                                            onChangeCallback={item => this.onChangePrescriptionPrinterType(item)}
                                            selectedItem={selectedPrescriptionType}
                                            styleForEachradio={{ width: "22%", float: "left", marginTop: "5px" }}
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("AppointmentConfirmationLabel")}</label></td>
                                <td className="c2">
                                    <div style={{ width: "600px" }}>
                                        <RadioButtonv2
                                            cssClass="settings-user-radioWidth"
                                            title={null}
                                            items={[
                                                new KeyValuePair("regular_b", cacheStore.GetString("RegularPrinterLabel")),
                                                new KeyValuePair("thermal_b", cacheStore.GetString("ThermicPrinterLabel"))
                                            ].toImmutable()}
                                            htmlName="appointment"
                                            onChangeCallback={item => this.onChangeAppointmentPrinterType(item)}
                                            changeOperation={null}
                                            selectedItem={employee.Model.IsRegularRDVConfirmPrinter === true ? "regular_b" : "thermal_b"}
                                            styleForEachradio={{ width: "22%", float: "left", marginTop: "5px" }}
                                        />
                                    </div>
                                </td>
                            </tr>

                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    private renderPermissions(): JSX.Element {
        const employee = this.props.editedUser;

        return (
            <PermissionSettingsSectionComponent
                disabled={false}
                onAllowlistChange={model => this.onAllowlistChange(model)}
                onPermissionCheckChange={(isChecked: boolean, permissions: Permission[]) => this.permissionsCheckChange(isChecked, permissions)}
                allowlist={employee.MedicalNoteAllowlist}
                allowlistChange={employee.MedicalNoteAllowlistChanges}
                permissionIds={this.isUsingRoleBasedPermissions() ? this.getResolvedRolePermissions(employee.Model.Roles, this.state.roleDefinitions) : employee.Model.Permissions}
            />
        );
    }

    private renderAccessSettings(showUserSettingsUiImprovements: boolean, showRoleBasedAccessControl: boolean): JSX.Element {
        const employee = this.props.editedUser;
        const allPermissions: Permission[] = Permission.getPermissionsByEmployee(employee);

        const patientTabPermission = allPermissions.find(permission => permission.Name === "PatientTabPermission");
        const showMedicalNoteAllowlist = patientTabPermission && cacheStore.ClinicSettings.EnableMedicalNoteProtection && employee.Model.Permissions.some(employeePermission => employeePermission.toString() == patientTabPermission.Id);
        let allSites = cacheStore.GetSites(null);
        let siteList: SitesModel[] = [];
        siteList = employee.getSites();

        let isSitesValidClass = siteList != null && siteList.length > 0 ? "" : "invalid-section";
        let isPermissionValidClass = employee.Model.Permissions != null && employee.Model.Permissions.length > 0 ? "" : "invalid-section";

        return (
            <div className="tbc">
                {!showUserSettingsUiImprovements && employee.Type !== EmployeeType.Api && (
                    <GroupSettingsComponent
                        onDeleteGroupCallback={groupId => this.onDeleteGroup(groupId)}
                        onGroupSelectCallback={groupItem => this.onGroupInfoSelect(groupItem)}
                        selectedGroups={employee.Model.Groups}
                        editable={true}
                    />
                )}
                <div>
                    <table>
                        <tbody>
                            {showRoleBasedAccessControl && (
                                <tr>
                                    <td className="c1" style={{ "paddingTop": "5px", "verticalAlign": "top" }}><label className={styles.rolesLabel}>{cacheStore.GetString("RoleBasedAccessControl_RolesLabel")}</label></td>
                                    <td className="c2">
                                        <RoleListFieldComponent
                                            dataCy="role-list-field"
                                            selectedRolesList={this.props.editedUser?.Model.Roles}
                                            setSelectedRoles={list => {
                                                this.props.mutateParentEmployee(emp => emp.Model.Roles = list);
                                            }}
                                            formMode={FormMode.Edit}
                                            showWarning={!this.isUsingRoleBasedPermissions()}
                                        />
                                    </td>
                                </tr>
                            )}
                            <tr data-cy="userProfile-settingsViewSites">
                                <td className="c1" style={{ "paddingTop": "5px", "verticalAlign": "top" }}><label className="form-label">{cacheStore.GetString("SitesLabel")}</label></td>
                                <td className={"c2 " + isSitesValidClass} style={{ "paddingTop": "5px", "verticalAlign": "top" }}>
                                    {allSites.map((x: SiteUIModel, i: number) => (
                                        <div key={`site_${i}`}>
                                            <CheckboxControl
                                                id={x.Id}
                                                onChange={(isChecked: boolean, id: Guid) => this.siteCheckChange(isChecked, id)}
                                                target={CheckboxTarget.Unassigned}
                                                label={x.Name}
                                                checked={(siteList && (siteList.some(z => z.Model.Id == x.Id))) ? true : false}
                                                dataCy={`userProfile-settingsViewSites-${i}`}
                                            />
                                        </div>
                                    ))}
                                </td>
                            </tr>
                            {(!showUserSettingsUiImprovements) && (
                                <tr>
                                    <td className="c1" style={{ "paddingTop": "5px", "verticalAlign": "top" }}><label className="form-label">{cacheStore.GetString("PermissionsLabel")}</label></td>
                                    <td className={"c2 " + isPermissionValidClass} style={{ "paddingTop": "5px", "verticalAlign": "top" }}>
                                        <div className={styles.permissionCheckboxesContainer}>
                                            {allPermissions.filter(x => !!x).map((permission: Permission, i: number) => (
                                                <div id={permission.Name} key={`perm_${i}`}>
                                                    <CheckboxControl
                                                        id={permission.Id}
                                                        onChange={(isChecked: boolean) => this.permissionsCheckChange(isChecked, [permission])}
                                                        target={CheckboxTarget.Unassigned}
                                                        label={EnumHelper.getVocab(EnumMyleLoginPermission, "Enums_EnumMyleLoginPermission_", permission.Name)}
                                                        checked={(employee.Model.Permissions && employee.Model.Permissions.some(employeePermission => employeePermission.toString() == permission.Id))}
                                                        dataCy={`permission-${permission.Name}`}
                                                    />
                                                </div>
                                            ))}
                                            {showMedicalNoteAllowlist && (
                                                <ProtectMedicalNoteComponent
                                                    onChange={model => this.onAllowlistChange(model)}
                                                    model={employee.MedicalNoteAllowlist}
                                                    modelChanges={employee.MedicalNoteAllowlistChanges}
                                                    editable
                                                />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    private renderServices(): JSX.Element {
        const employee = this.props.editedUser;
        const myleHelper = new MyleHelper();
        const columnsOptions = myleHelper.loadTableColumnOptionsFromSettings(this.saveOptionKey, this.getMyleTableColumnsDefaultOptions());
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);
        const tableColumns = this.getTableColumns(columnsOptions);

        let serviceFilteredAdHoc: ServiceOrComboTableItem<IComboOrServiceItem>[] = employee.ServicesCombos
            ? employee.ServicesCombos.filter(x => x.IsAdHoc == false)
            : null;

        return (
            <div>
                {employee.ServicesCombos != null && employee.ServicesCombos.length > 0 && (
                    <MyleTable
                        TableRenderOptions={tableRenderOptions}
                        TableId="userConfigtable_services"
                        Columns={tableColumns}
                        DataSource={Immutable.fromJS(serviceFilteredAdHoc)}
                        RowClickOperation={null}
                        SaveOptionskeyTarget={this.saveOptionKey}
                        ColumnReorderTarget={MyleTableTarget.Unassigned}
                        ColumnSortTarget={MyleTableTarget.Unassigned}
                        NoScrollLimit={true}
                        CssClass="admin-table-override"
                    />
                )}

                {(employee.ServicesCombos == null || employee.ServicesCombos.length == 0) && (
                    <span style={{ "marginLeft": "20px" }}>
                        {cacheStore.GetString("UserNoServiceLabel")}
                    </span>
                )}
            </div>
        );
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<ServiceOrComboTableItem<IComboOrServiceItem>>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "Site":
                        return {
                            renderCell: item => (
                                <div><SpanControl text={item.Site} /> </div>
                            )
                        };

                    case "Service":
                        return {
                            renderCell: item => (
                                <div><SpanControl text={item.Name} /> </div>
                            )
                        };

                    case "Type":
                        return {
                            renderCell: item => (
                                <div> <SpanControl text={item.TypeString} /> </div>
                            )
                        };

                    case "Speciality":
                        return {
                            renderCell: item => (
                                <div> <SpanControl text={item.Speciality} /> </div>
                            )
                        };

                    case "Duration":
                        return {
                            renderCell: item => (
                                <div> <SpanControl text={item.Duration + " min"} />
                                    <span style={{ "width": "50%", "float": "right" }}><a onClick={() => this.onDeleteServiceCombo(item)} style={this.redx}> X</a></span>
                                </div>
                            )
                        };
                }
            },
            false
        );
    }

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        var cols: MyleTableColumnOptions[] = [];

        var c_opt0 = new MyleTableColumnOptions();
        c_opt0.HeaderText = cacheStore.GetString("SiteDLabel");
        c_opt0.FieldName = "Site";
        c_opt0.IsFixedInPlace = true;
        c_opt0.IsSortable = true;
        c_opt0.RowClickEnabled = true;
        c_opt0.SortColumn = AdminServiceGridSortColumn.Site;
        c_opt0.SortDirection = SortDirection.ASC;
        c_opt0.WidthPercentageDefault = 25;

        var c_opt1 = new MyleTableColumnOptions();
        c_opt1.HeaderText = cacheStore.GetString("NameLabel");
        c_opt1.FieldName = "Service";
        c_opt1.RowClickEnabled = true;
        c_opt1.IsSortable = true;
        c_opt1.SortColumn = AdminServiceGridSortColumn.Name;
        c_opt1.SortDirection = SortDirection.ASC;
        c_opt1.WidthPercentageDefault = 20;

        var c_opt2 = new MyleTableColumnOptions();
        c_opt2.HeaderText = cacheStore.GetString("TypeLabel");
        c_opt2.FieldName = "Type";
        c_opt2.RowClickEnabled = true;
        c_opt2.IsSortable = true;
        c_opt2.SortColumn = AdminServiceGridSortColumn.Type;
        c_opt2.SortDirection = SortDirection.ASC;
        c_opt2.WidthPercentageDefault = 20;

        var c_opt3 = new MyleTableColumnOptions();
        c_opt3.HeaderText = cacheStore.GetString("SpecialityLabel");
        c_opt3.FieldName = "Speciality";
        c_opt3.RowClickEnabled = true;
        c_opt3.IsSortable = true;
        c_opt3.SortColumn = AdminServiceGridSortColumn.Speciality;
        c_opt3.SortDirection = SortDirection.ASC;
        c_opt3.WidthPercentageDefault = 25;

        var c_opt4 = new MyleTableColumnOptions();
        c_opt4.HeaderText = cacheStore.GetString("DurationLabel");
        c_opt4.FieldName = "Duration";
        c_opt4.RowClickEnabled = true;
        c_opt4.IsSortable = true;
        c_opt4.SortColumn = AdminServiceGridSortColumn.Duration;
        c_opt4.SortDirection = SortDirection.ASC;
        c_opt4.WidthPercentageDefault = 15;

        cols.push(c_opt0);
        cols.push(c_opt1);
        cols.push(c_opt2);
        cols.push(c_opt3);
        cols.push(c_opt4);

        return cols;
    }

    private loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        var defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: false,
            Autoresize: true,
            OnColumnWidthChange: null
        };

        return Immutable.fromJS(defaultTablePluginOptions);
    }

    private mutateState(mutation: (s: UserConfigurationInfoEditComponentState) => void, then?: () => void): void {
        const s = { ...this.state };
        mutation(s);

        s.canSaveSettings = this.isValid(s);
        let previousCanSaveSettings = this.state ? this.state.canSaveSettings : false;

        this.setState(s, () => {
            if (this.props.isValidCallBack != null && previousCanSaveSettings != s.canSaveSettings)
                this.props.isValidCallBack(s.canSaveSettings);

            if (then != null)
                then();
        });
    }

    private onChangeLabels(item: KeyValuePair<string, string>): void {
        this.props.mutateParentEmployee(emp => {
            if (item.Key === "landscape")
                emp.Model.PrintingSetting = true;
            else
                emp.Model.PrintingSetting = false;
        });
    }

    private onChangeLargeLabelPrinting(item: KeyValuePair<string, string>): void {
        this.props.mutateParentEmployee(emp => {
            if (item.Key === "small")
                emp.Model.LargeLabelPrinting = false;
            else
                emp.Model.LargeLabelPrinting = true;
        });
    }

    private onChangePrescriptionPrinterType(item: KeyValuePair<string, string>): void {
        this.props.mutateParentEmployee(emp => {
            if (item.Key === "regular_a")
                emp.Model.PrescriptionPrintType = PrescriptionPrintType.Regular;
            else if (item.Key === "thermal_a")
                emp.Model.PrescriptionPrintType = PrescriptionPrintType.Thermal;
            else if (item.Key === "mexico_a")
                emp.Model.PrescriptionPrintType = PrescriptionPrintType.Mexico;
        });
    }

    private onChangeAppointmentPrinterType(item: KeyValuePair<string, string>): void {
        if (item != null) {
            this.props.mutateParentEmployee(emp => {
                if (item.Key === "regular_b")
                    emp.Model.IsRegularRDVConfirmPrinter = true;
                else
                    emp.Model.IsRegularRDVConfirmPrinter = false;
            });
        }
    }

    private siteCheckChange(isChecked: boolean, siteId: Guid): void {
        this.props.mutateParentEmployee(emp => {
            if (emp.Model.AssociatedSites == null)
                emp.Model.AssociatedSites = [];

            if (isChecked && !emp.Model.AssociatedSites.some(s => s == siteId)) {
                // Adding Site
                emp.Model.AssociatedSites.push(siteId);
            }
            else if (!isChecked && emp.Model.AssociatedSites.some(s => s == siteId)) {
                // removing site
                const servicesOffered = this.props.editedUser.Model.ServicesOffered;
                const hasServicesAtSite = servicesOffered?.any(service => service.ServiceDefinitionModel.Model.SiteId === siteId);

                // warn if the user has services configured at the site
                if (hasServicesAtSite) {
                    sessionStore.showPopup({
                        title: cacheStore.GetString("WarningLabel"),
                        content: cacheStore.GetString("RemoveSiteWarning"),
                        showCancel: true,

                        okLabel: cacheStore.GetString("ContinueLabel"),
                        okAction: () => this.removeServicesForAssociatedSite(siteId)
                    });
                }
                else {
                    this.removeSiteFromFrontEnd(emp, siteId);
                }
            }

            // set first selected site as activeSiteId if employee has none or if the previous active site is not in the AssociatedSites list anymore
            if ((!emp.Model.ActiveSiteId?.length || !emp.Model.AssociatedSites.includes(emp.Model.ActiveSiteId)) && emp.Model.AssociatedSites.length)
                emp.Model.ActiveSiteId = emp.Model.AssociatedSites[0];
        });
    }

    private removeSiteFromFrontEnd(emp: EmployeeUIModel, siteId: string): void {
        emp.Model.AssociatedSites = emp.Model.AssociatedSites.filter(p => p !== siteId);
    }

    private removeServicesForAssociatedSiteFromFrontEnd(siteId: string, siteServices: ServiceModel[]): void {
        this.props.mutateParentEmployee(emp => {
            this.removeSiteFromFrontEnd(emp, siteId);
            emp.Model.ServicesOffered = emp.Model.ServicesOffered.filter(s => !siteServices.some(rem => rem.ServiceID
                ? rem.ServiceID == s.ServiceID
                : rem.ServiceDefinitionModel.Model.Id == s.ServiceDefinitionModel.Model.Id
            ));
            emp.refreshServiceCombos();
        });
    }

    private removeServicesForAssociatedSite(siteId: string): void {
        let siteName = cacheStore.GetSiteById(siteId).Model.ClinicalName;

        let siteServices = this.props.editedUser.Model.ServicesOffered.filter(c => c.ServiceDefinitionModel.Model.SiteId === siteId);
        let newSiteServices = siteServices.filter(c => !!c.ServiceID);
        if (newSiteServices.length === 0) {
            this.removeServicesForAssociatedSiteFromFrontEnd(siteId, siteServices);
            return;
        }
        Client.Post(
            ServerOperations.Server.Myle_User_ValidateIfServiceCurrentlyUsed,
            {
                SiteId: siteId,
                EmployeeContactId: this.props.editedUser.ContactId,
                EmployeeContactFacetId: this.props.editedUser.ContactFacetId,
                ServiceDefinitionIds: newSiteServices.map(c => c.ServiceDefinitionModel.Model.Id),
                ServiceIds: newSiteServices.map(c => c.ServiceID)
            },
            response => {
                if (response.Errors != null && response.Errors.length > 0) {
                    sessionStore.showPopup(response.Errors[0]);
                    return;
                }
                if (response.mayBeDisabled != true) {
                    sessionStore.showPopup(cacheStore.GetString("ServiceAreStillInUseWarning").replaceAll("{Site}", siteName));
                    return;
                }

                this.removeServices(
                    newSiteServices.map(c => c.ServiceID),
                    () => this.removeServicesForAssociatedSiteFromFrontEnd(siteId, siteServices)
                );
            }
        );
    }

    public removeServices(servicesIdsToRemove: string[], onSuccess: (response) => void): void {
        // Service is not used, we can disable it for them
        Client.Post(
            ServerOperations.Server.Myle_User_DisableServices,
            {
                EmployeeContactFacetId: this.props.editedUser.ContactFacetId,
                ServicesId: servicesIdsToRemove
            },
            response => {
                if (!response.IsSuccess) {
                    sessionStore.showPopup("?");
                    return;
                }

                onSuccess(response);
            }
        );
    }

    private onAllowlistChange(model: IAllowlistModel): void {
        this.props.mutateParentEmployee(emp => {
            emp.MedicalNoteAllowlistChanges = { ...model };
        });
    }

    private permissionsCheckChange(isChecked: boolean, permissions: Permission[]): void {
        this.props.mutateParentEmployee(emp => {
            const currentPermissions = this.isUsingRoleBasedPermissions() ? this.getResolvedRolePermissions(emp.Model.Roles, this.state.roleDefinitions) : emp.Model.Permissions;
            emp.Model.Permissions = PermissionDependencyResolver.ResolvePermissionChangesAndDependencies(currentPermissions, permissions, isChecked);
        });
    }

    private onDeleteGroup(id: Guid): void {
        this.props.mutateParentEmployee(emp => {
            let group: EmployeeGroup = new EmployeeGroup;
            group.Id = id;
            emp.Model.Groups = emp.Model.Groups.filter(x => x.Id !== group.Id);
        });
    }

    private onGroupInfoSelect(item: EmployeeGroup): void {
        let previousGroup = [];
        if (item != null)
            this.props.mutateParentEmployee(emp => {
                if (emp.Model.Groups) {
                    if (emp.Model.Groups.length > 0)
                        previousGroup = emp.Model.Groups.filter(x => x.Name == item.Name);

                    if (previousGroup.length == 0)
                        emp.Model.Groups.push(item);
                }
                else {
                    emp.Model.Groups = [];
                    emp.Model.Groups.push(item);
                }
            });
    }

    private onDeleteServiceCombo(item: ServiceOrComboTableItem<IComboOrServiceItem>): void {
        this.props.mutateParentEmployee(emp => emp.removeService(item.ServiceDefinitionId));
    }

    private isValid(s: UserConfigurationInfoEditComponentState): boolean {
        let isValid: boolean = false;
        isValid = s.isEndDateValid && s.isStartDateValid && s.isSiteValid && s.isPermissionValid && s.isInactivityTimeoutValid;
        if (this.props.model.get("UserEditMode") == UserEditMode.Create) {
            const isTemporaryPasswordValid = s.isTemporaryPasswordValid || cacheStore.ClinicSettings.EnableSSOAuthentication;
            isValid = s.isEndDateValid && s.isStartDateValid && s.isUserNameValid && isTemporaryPasswordValid && s.isSiteValid && s.isPermissionValid && s.isInactivityTimeoutValid;
        }
        return isValid;
    }

    private permissionsByDefault(user: Employee): Employee {
        let allPermissions: Permission[] = [];
        user.Permissions = [];
        allPermissions = cacheStore.Permissions;
        allPermissions.map((x: Permission) => {
            if (x.Name == "CalendarTabPermission" || x.Name == "PatientTabPermission" || x.Name == "CommunicationsTabPermission")
                user.Permissions.push(x.Id);
        });

        return user;
    }

    private getUserConfigurationLanguage(employee: EmployeeUIModel): void {
        let lookupCode = cacheStore.GetLanguagesLookup().firstOrDefault(lookup => lookup.LookupID === employee.Model.PreferredOfficialLanguageLookupId)?.LookupCode;
        switch (lookupCode) {
            case "FRENCH":
                employee.Model.Language = "fr";
                break;
            case "SPANISH":
                employee.Model.Language = "es-MX";
                break;
            default:
                employee.Model.Language = "en";
                break;
        }
    }

    private isUsingRoleBasedPermissions(): boolean {
        if (!cacheStore.ClinicSettings.EnableRoleBasedAccessControl)
            return false;

        const employee = this.props.editedUser;
        return !employee.Model.Permissions?.any();
    }

    private getPermissionsHeaderActionButton(): JSX.Element {
        if (!cacheStore.ClinicSettings.EnableRoleBasedAccessControl)
            return null;

        if (this.isUsingRoleBasedPermissions())
            return null;

        return (
            <button
                className={styles.buttonAsActionLink}
                onClick={() =>
                    this.props.mutateParentEmployee(emp => {
                        emp.Model.Permissions = [];
                    })
                }
            >
                {cacheStore.GetString("RoleBasedAccessControl_ResetRolePermissionsLabel")}
            </button>
        );
    }

    private async loadRolePermissionHeaders(): Promise<void> {
        const editedUser = this.props.editedUser.Model;
        const roleDefinitions = await RolesApi.GetCachedRoleHeadersAsync();
        const rolePermissions = this.getResolvedRolePermissions(editedUser.Roles, roleDefinitions.RoleHeaders);
        this.mutateState(s => {
            s.roleDefinitions = roleDefinitions.RoleHeaders;
            s.isPermissionValid = editedUser.Permissions?.any() || rolePermissions?.any();
        });
    }

    private getResolvedRolePermissions(selectedRoles: string[], roleDefinitions: RoleHeaderModel[]): string[] {
        if (!roleDefinitions || !selectedRoles)
            return [];

        const allPermissions = selectedRoles.map(role => roleDefinitions
            .firstOrDefault(definition => definition.Id === role)
            .PermissionIds
        );

        return [].concat(...allPermissions).distinct();
    }
}

export { UserConfigurationInfoEditComponent };
